import { ServiceKitStore } from '../ServiceKits/ServiceKitStore';
import { AdminStore } from '../Admin/AdminStore';
import { AlertActionsStore } from '../DisplayAlerts/AlertActionsStore';
import { AppStore } from '../Core/AppStore';
import { AuditStore } from '../Audit/AuditStore';
import {
    bind,
    CommonAppStore,
    ErrorStore,
    HttpClient,
    PowerBIStore,
    TasksStore,
    TicketsStore,
    TooltipsStore,
} from 'Mri.Web.Common';
import { DisplayConfigurationStore } from '../Displays/DisplayView/Settings/DisplayConfigurationStore';
import { DisplayEventProfileStore } from '../DisplayEventProfiles/DisplayEventProfileStore';
import { DisplayFiltersStore } from '../DisplayFilters/DisplayFiltersStore';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { DisplaysChartsStore } from '../Displays/DisplayView/LiveCharts/DisplaysChartsStore';
import { FileSystemsStore } from '../SoftwareUpdates/FileSystemsStore';
import { ModelDefinitionsStore } from '../ModelDefinitons/ModelDefinitionsStore';
import { PartDefinitionsStore } from '../PartDefinitions/PartDefinitionsStore';
import { ProductionProjectsStore } from '../ProductionProjects/ProductionProjectsStore';
import { UnitsStore } from '../Units/UnitsStore';
import { NocServersStore } from '../NocServers/NocServersStore';
import { DisplayGroupsStore } from '../DisplayGroups/DisplayGroupsStore';
import { CsvStore } from '../Csv/CsvStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { ContractsStore } from '../Contracts/ContractsStore';
import { ManifestsStore } from '../SoftwareUpdates/ManifestsStore';
import { WarehouseStore } from '../Warehouses/WarehouseStore';
import { WeatherStore } from '../Weather/WeatherStore';
import { CloudConfigsStore } from '../CloudConfigs/CloudConfigsStore';
import { PartsStore } from '../Parts/PartsStore';
import { WarrantyAlertsStore } from '../WarrantyAlerts/WarrantyAlertsStore';
import { UserManagerSettings } from 'oidc-client';
import { AccountStore } from '../Account/AccountStore';
import { AlertStore } from '../Admin/ContractExpirationAlerts/AlertStore';
import {runInAction, toJS} from "mobx";
import {cloneDeep} from "lodash";
import { diff } from 'deep-diff';

const httpClient = new HttpClient();
const initialOidcConfig = (window as any).initialOidcConfig as UserManagerSettings;
const accountStore = new AccountStore(httpClient, initialOidcConfig);
const appStore = new AppStore();

setupStorageSync(accountStore, appStore);

httpClient.setAccessTokenStore(accountStore);
export const stores = {
    accountStore,
    appStore,
    serviceKitStore: new ServiceKitStore(httpClient),
    adminStore: new AdminStore(httpClient),
    alertActionsStore: new AlertActionsStore(httpClient, initialOidcConfig),
    auditStore: new AuditStore(httpClient),
    commonAppStore: new CommonAppStore(httpClient),
    displayConfigurationStore: new DisplayConfigurationStore(httpClient),
    displayEventProfileStore: new DisplayEventProfileStore(httpClient),
    displayFiltersStore: new DisplayFiltersStore(httpClient),
    displaysStore: new DisplaysStore(httpClient, accountStore, appStore),
    displaysChartsStore: new DisplaysChartsStore(httpClient),
    errorStore: new ErrorStore(httpClient),
    fileSystemsStore: new FileSystemsStore(httpClient, accountStore),
    modelDefinitionsStore: new ModelDefinitionsStore(httpClient),
    partDefinitionsStore: new PartDefinitionsStore(httpClient),
    powerBIStore: new PowerBIStore(httpClient),
    productionProjectsStore: new ProductionProjectsStore(httpClient),
    unitsStore: new UnitsStore(httpClient),
    ticketsStore: new TicketsStore(httpClient, (window as any).initialFieldIds),
    tasksStore: new TasksStore(httpClient, accountStore),
    nocServersStore: new NocServersStore(httpClient),
    displayGroupsStore: new DisplayGroupsStore(httpClient),
    csvStore: new CsvStore(httpClient, accountStore),
    accountsStore: new AccountsStore(httpClient),
    contractsStore: new ContractsStore(httpClient),
    manifestsStore: new ManifestsStore(httpClient, accountStore),
    tooltipsStore: new TooltipsStore(httpClient),
    warehouseStore: new WarehouseStore(httpClient),
    weatherStore: new WeatherStore(httpClient),
    cloudConfigsStore: new CloudConfigsStore(httpClient),
    partDefsStore: new PartDefinitionsStore(httpClient),
    partsStore: new PartsStore(httpClient),
    warrantyAlertsStore: new WarrantyAlertsStore(httpClient),
    contractExpirationAlertStore: new AlertStore(httpClient),
};

function setupStorageSync(accountStore: AccountStore, appStore: AppStore) {
    window.addEventListener('storage', (event) => {
        // Prevents updates if it is the active tab
        if (document.hasFocus() || document.visibilityState === 'visible') {
            return;
        }


        if (event.key === 'AccountStore.AccountStorage' && event.newValue) {
            try {
                const newStorage = JSON.parse(event.newValue);
                const oldStorage = cloneDeep(toJS(accountStore.storage)); // snapshot before update

                runInAction(() => {
                    accountStore.suppressStorageSync = true;
                    Object.assign(accountStore.storage, newStorage);
                    queueMicrotask(() => {
                        accountStore.suppressStorageSync = false;
                    });
                });

                // Log granular differences
                setTimeout(() => {
                    const updatedStorage = toJS(accountStore.storage);
                    const differences = diff(oldStorage, updatedStorage);

                    if (differences && differences.length > 0) {
                        console.log("🔍 Detailed storage changes:");
                        differences.forEach(change => {
                            console.log(`• [${change.kind}] ${change.path?.join('.')}:`, change);
                        });
                    } else {
                        console.log("✅ No actual change in storage detected.");
                    }
                }, 0);

            } catch (e) {
                console.error('Failed to sync AccountStore from localStorage:', e);
            }
        }

        if (event.key === 'AppStore.AppStorage' && event.newValue) {
            try {
                const newStorage = JSON.parse(event.newValue);
                if (newStorage.version !== appStore.storage.version) {
                    console.warn('App version mismatch detected. Reloading...');
                    window.location.reload();
                }
            } catch (e) {
                console.error('Failed to sync AppStore from localStorage:', e);
            }
        }
    });
}